/**
 * DepartmentsCard Component
 *
 * Admin/HR view - displays all departments in a grid
 */

import React from 'react';
import { Box, Typography, Button, Grid } from '@mui/material';
import { Business, Add } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { CollapsibleCard } from '../shared/CollapsibleCard';
import { EmptyState } from '../shared/EmptyState';
import { getScoreColor } from '../utils/scoreUtils';

export const DepartmentsCard: React.FC = () => {
    const { isDarkMode, data, permissions, sections, dialogs, filteredObjectives } = useOKRDashboardContext();
    const { departments } = data;
    const { expandedSections, toggleSection } = sections;

    // Only show for Admin/HR
    if (!permissions.canViewAllTeams) {
        return null;
    }

    return (
        <CollapsibleCard
            icon={<Business sx={{ color: '#fff', fontSize: 24 }} />}
            iconBgGradient="linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)"
            title="Teams & Departments"
            subtitle="All organization teams"
            expanded={expandedSections.departments}
            onToggle={() => toggleSection('departments')}
            isDarkMode={isDarkMode}
            count={departments.length}
        >
            {departments.length === 0 ? (
                <EmptyState
                    title="No Departments"
                    description="Create departments to organize your teams"
                    actionLabel={permissions.canCreateTeam ? 'Create Department' : undefined}
                    onAction={permissions.canCreateTeam ? dialogs.openCreateTeamDialog : undefined}
                    isDarkMode={isDarkMode}
                    compact
                />
            ) : (
                <Grid container spacing={2}>
                    {departments.map((dept) => {
                        // Handle both API formats: dept_id/dept_name and id/name
                        const deptId = dept.dept_id ?? dept.id;
                        const deptName = dept.dept_name ?? dept.name;

                        const deptObjective = filteredObjectives.team.find(
                            (t) => Number(t.department_id) === Number(deptId)
                        );
                        const score = deptObjective?.score || 0;
                        const scoreColor = getScoreColor(score);

                        return (
                            <Grid size={{ xs: 12, sm: 6 }} key={deptId}>
                                <Box
                                    sx={{
                                        p: 2.5,
                                        borderRadius: 2,
                                        bgcolor: isDarkMode ? '#1e293b40' : '#f8fafc',
                                        border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: isDarkMode ? '#1e293b80' : '#f1f5f9',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box
                                                sx={{
                                                    width: 44,
                                                    height: 44,
                                                    borderRadius: 2,
                                                    bgcolor: `${scoreColor}15`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: `2px solid ${scoreColor}25`,
                                                }}
                                            >
                                                <Business sx={{ color: scoreColor, fontSize: 24 }} />
                                            </Box>
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: '1rem',
                                                        color: isDarkMode ? '#f1f5f9' : '#1e293b',
                                                    }}
                                                >
                                                    {deptName}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: isDarkMode ? '#64748b' : '#94a3b8',
                                                    }}
                                                >
                                                    {dept.memberCount ?? dept.member_count ?? 0} members
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box
                                            sx={{
                                                bgcolor: scoreColor,
                                                color: '#fff',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                fontSize: '0.85rem',
                                                boxShadow: `0 2px 6px ${scoreColor}40`,
                                            }}
                                        >
                                            {Math.round(score)}%
                                        </Box>
                                    </Box>
                                    {deptObjective && (
                                        <Typography
                                            sx={{
                                                mt: 2,
                                                fontSize: '0.85rem',
                                                color: isDarkMode ? '#94a3b8' : '#64748b',
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {deptObjective.title}
                                        </Typography>
                                    )}
                                    {!deptObjective && permissions.canCreateTeamOKR && (
                                        <Button
                                            size="small"
                                            startIcon={<Add />}
                                            onClick={() => dialogs.openCreateTeamObjDialog(deptId)}
                                            sx={{
                                                mt: 2,
                                                bgcolor: 'rgba(99, 102, 241, 0.15)',
                                                color: '#818cf8',
                                                fontWeight: 600,
                                                fontSize: '0.75rem',
                                                textTransform: 'none',
                                                '&:hover': {
                                                    bgcolor: 'rgba(99, 102, 241, 0.25)',
                                                },
                                            }}
                                        >
                                            Create Team Objective
                                        </Button>
                                    )}
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </CollapsibleCard>
    );
};

export default DepartmentsCard;
